import {Ref, ref, reactive} from 'vue';
import {defineStore} from 'pinia';

import {Viewer} from 'neuroglancer/viewer';
import {defaultCredentialsManager} from 'neuroglancer/credentials_provider/default_manager';
import {MiddleAuthCredentialsProvider} from 'neuroglancer/datasource/middleauth/credentials_provider';
import {cancellableFetchSpecialOk, parseSpecialUrl} from 'neuroglancer/util/special_protocol_request';
import {responseJson} from 'neuroglancer/util/http_request';

import {Config, EYEWIRE_II_CAVE_CONFIG, CHAT_WS_URL} from './config';
import {supabase} from './supabase';
import {SegmentationUserLayer} from "neuroglancer/segmentation_user_layer";
import {parsePositionString} from "neuroglancer/ui/default_clipboard_handling";

declare const CONFIG: Config|undefined;
declare const DEFAULT_SETTINGS: {  [key: string]: any }

export const useDropdownListStore = defineStore('dropdownlist', () => {
  let dropdownCount = 0;

  const activeDropdowns = reactive({} as {[group: string]: number|undefined});

  function getDropdownId() {
    dropdownCount++;
    return dropdownCount;
  }

  return {getDropdownId, activeDropdowns};
});

export interface loginSession {
  key: string,
  name: string,
  email: string,
  hostname: string,
  status?: number,
}

export const useLoginStore = defineStore('login', () => {
  const TOKEN_PREFIX = 'auth_token_v2_';

  async function logout(session: loginSession) {
    window.localStorage.removeItem(session.key);
    const login_url = session.key.split(TOKEN_PREFIX)[1] as string|undefined;
    if (!login_url) return;
    const provider = defaultCredentialsManager.getCredentialsProvider('middleauth', login_url) as MiddleAuthCredentialsProvider;
    if (provider) {
      provider.updateCachedGet();
    }
    sessions.value = sessions.value.filter(x => x.key !== session.key);
  }

  async function update() {
    const localStorageKeys: string[] = [];
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith(TOKEN_PREFIX)) {
        localStorageKeys.push(key);
      }
    }

    const newSessions: loginSession[] = [];

    for (const key of localStorageKeys) {
      const login_url = key.split(TOKEN_PREFIX)[1] as string|undefined;
      if (!login_url) continue;
      const provider = defaultCredentialsManager.getCredentialsProvider('middleauth', login_url) as MiddleAuthCredentialsProvider;
      if (!provider) continue;

      const dataString = localStorage.getItem(key);
      if (!dataString) { return; }
      const data = JSON.parse(dataString);
      const {hostname} = new URL(data.url);

      try {
        const res = await fetch(data.url + '/api/v1/user/me', {
          headers: {
            "Authorization": `Bearer ${data.accessToken}`,
          }
        });
        if (res.status === 200) {
          const contentType = res.headers.get("content-type");
          const message = await ((contentType === 'application/json') ? res.json() : res.text());
          newSessions.push({
            key,
            name: message.name,
            email: message.email,
            hostname,
          });
        } else {
          newSessions.push({
            key,
            name: '',
            email: '',
            hostname,
            status: res.status,
          });
        }
      } catch (e) {
        e;
        // newSessions.push({
        //   name: '',
        //   email: '',
        //   hostname,
        //   error: e,
        // });
      }
    }

    sessions.value = newSessions;
  }
  const sessions: Ref<loginSession[]> = ref([]);
  return {sessions, update, logout};
});

export interface Volume {
  name: string,
  description: string,
  image_layers: Layer[],
  segmentation_layers: Layer[],
}

interface Layer {
  source: string,
  ngl_image_name?: string,
  name: string,
  description: string,
  type: string,
}


export const useLayersStore = defineStore('layers', () => {
  const activeLayers: Set<string> = reactive(new Set());

  let viewer: Viewer|undefined = undefined;
  let segEditCleanup: (() => void) | null = null;

  function refreshLayers() {
    if (!viewer) return;
    activeLayers.clear();
    const layers = viewer.layerManager.managedLayers;
    for (const layer of layers) {
      if (!layer.layer) {
        console.log('does this ever happen?');
        continue;
      }
      const dataSources = layer.layer.dataSources;
      for (const source of dataSources) {
        activeLayers.add(source.spec.url.replace('middleauth+', ''));
      }
    }
    // Re-attach segment edit watcher whenever layers change
    watchSegmentEdits();
  }

  /**
   * Watch visible segments on the first SegmentationUserLayer.
   * Fires whenever the user merges, splits, or selects new segments.
   *
   * Heuristics:
   *   - segment count decreased → merge (fewer segments = segments were combined)
   *   - segment count increased → split or new selection
   *
   * Also increments cellsSubmitted every ~5 edits to animate the cell-dot canvas.
   */
  function watchSegmentEdits() {
    if (!viewer) return;

    // Clean up previous listener
    if (segEditCleanup) { segEditCleanup(); segEditCleanup = null; }

    const segLayer = viewer.layerManager.managedLayers.find(
      x => x.layer instanceof SegmentationUserLayer,
    );
    if (!segLayer || !(segLayer.layer instanceof SegmentationUserLayer)) return;

    const groupState = (segLayer.layer as SegmentationUserLayer)
      .displayState.segmentationGroupState.value;
    const visibleSegs = groupState.visibleSegments;

    let prevCount = visibleSegs.size;
    let localEditAccum = 0;
    let pendingNetDiff = 0;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingRemoved = new Set<string>(); // specific segments removed during this operation
    const pendingAdded   = new Set<string>(); // specific segments added during this operation
    const EDIT_DEBOUNCE_MS = 1500;

    // Signal fires (segId, wasAdded) — track the specific segments involved
    const handler = (changedId?: any, wasAdded?: boolean) => {
      // Capture the specific segment IDs from the signal
      try {
        if (changedId != null) {
          const ids = Array.isArray(changedId) ? changedId : [changedId];
          for (const id of ids) {
            const str = id.toString();
            if (wasAdded) pendingAdded.add(str);
            else pendingRemoved.add(str);
          }
        }
      } catch { /* Uint64 toString may fail */ }

      const newCount = visibleSegs.size;
      if (newCount === prevCount) return;

      // Accumulate net segment change (merge = negative, split = positive)
      pendingNetDiff += (newCount - prevCount);
      prevCount = newCount;

      // Debounce: group rapid segment changes from a single merge/split
      // into one edit event (visibleSegments.changed fires multiple times
      // as root IDs update during a single graph operation)
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const net = pendingNetDiff;
        pendingNetDiff = 0;
        // Grab & clear the tracked segment IDs for this operation
        const removedIds = pendingRemoved.size ? [...pendingRemoved].join(',') : null;
        const addedIds   = pendingAdded.size   ? [...pendingAdded].join(',')   : null;
        pendingRemoved.clear();
        pendingAdded.clear();
        if (net === 0) return; // changes cancelled out

        const statsStore = useUserStatsStore();
        // Count as exactly 1 operation regardless of segment count change
        if (net < 0) {
          statsStore.setStats({
            editsAllTime:   statsStore.stats.editsAllTime   + 1,
            mergesAllTime:  statsStore.stats.mergesAllTime  + 1,
            editsThisWeek:  statsStore.stats.editsThisWeek  + 1,
            mergesThisWeek: statsStore.stats.mergesThisWeek + 1,
            editsThisMonth: statsStore.stats.editsThisMonth + 1,
            mergesThisMonth:statsStore.stats.mergesThisMonth+ 1,
            editsToday:     statsStore.stats.editsToday     + 1,
            mergesToday:    statsStore.stats.mergesToday     + 1,
          });
        } else {
          statsStore.setStats({
            editsAllTime:   statsStore.stats.editsAllTime   + 1,
            splitsAllTime:  statsStore.stats.splitsAllTime  + 1,
            editsThisWeek:  statsStore.stats.editsThisWeek  + 1,
            splitsThisWeek: statsStore.stats.splitsThisWeek + 1,
            editsThisMonth: statsStore.stats.editsThisMonth + 1,
            splitsThisMonth:statsStore.stats.splitsThisMonth+ 1,
            editsToday:     statsStore.stats.editsToday     + 1,
            splitsToday:    statsStore.stats.splitsToday    + 1,
          });
        }

        statsStore.logDailyEdit(net < 0 ? 'merge' : 'split');
        statsStore.signalEdit(net < 0 ? 'merge' : 'split');
        localEditAccum += 1;
        if (localEditAccum >= 5) {
          statsStore.setStats({ cellsSubmitted: statsStore.stats.cellsSubmitted + 1 });
          localEditAccum = 0;
        }

        // Log to Supabase with diff=1 (one operation)
        try {
          const backendStore = useProofreadingBackendStore();
          if (backendStore.userId) {
            const viewer: any = (window as any)['viewer'];
            const pos = viewer?.navigationState?.position?.value;
            const coords = pos ? `${pos[0]}, ${pos[1]}, ${pos[2]}` : null;
            const operation = net < 0 ? 'merge' : 'split';
            backendStore.logEdit({
              operation,
              segment_before: removedIds,  // segments consumed/removed by the operation
              segment_after: addedIds,     // segments produced/added by the operation
              coordinates: coords,
              metadata: { net_segment_change: net, diff: 1 },
            });
            backendStore.postActivity(`${operation === 'merge' ? 'merged' : 'split'} segment`);
          }
        } catch { /* backend logging is non-critical */ }

        // Update Brain Quest segment lineage
        try {
          const queueStore = useProofreadingQueueStore();
          const removed = removedIds ? removedIds.split(',') : [];
          const added   = addedIds   ? addedIds.split(',')   : [];
          queueStore.onSegmentEdit(removed, added);
        } catch { /* non-critical */ }
      }, EDIT_DEBOUNCE_MS);
    };

    visibleSegs.changed.add(handler);
    segEditCleanup = () => {
      visibleSegs.changed.remove(handler);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }

  function initializeWithViewer(v: Viewer) {
    viewer = v;

    // set default values in settings
    viewer.chunkQueueManager.capacities.gpuMemory.sizeLimit.value = 2e9;
    viewer.chunkQueueManager.capacities.systemMemory.sizeLimit.value = 3e9;
    viewer.layout.restoreState('xy-3d');
    viewer.layerManager.layersChanged.add(refreshLayers);
    refreshLayers();
  }

  async function selectLayers(layers: any[]) {
    if (!viewer) return;
    viewer.layerSpecification.restoreState(layers);
    viewer.navigationState.reset();

    const segmentationLayer = viewer.layerManager.managedLayers.filter(
        (x) => x.layer instanceof SegmentationUserLayer
    )[0];
    if (segmentationLayer) {
      const segmentationLayerName = segmentationLayer.name;
      const SETTINGS = DEFAULT_SETTINGS[segmentationLayerName]
      viewer!.coordinateSpace.restoreState({
        x: [SETTINGS.dimensions[0], "m"],
        y: [SETTINGS.dimensions[1], "m"],
        z: [SETTINGS.dimensions[2], "m"],
      });

      const position = parsePositionString(SETTINGS.position, 3);
      if (position !== undefined) {
        viewer!.navigationState.position.value = position;
      }
      viewer!.crossSectionScale.value = SETTINGS.crossSectionScale;
      viewer!.projectionScale.value = SETTINGS.projectionScale;
      viewer!.projectionOrientation.restoreState(SETTINGS.projectionOrientation);
    }
  }

  /**
   * Derives the CAVE server base URL from the active neuroglancer state.
   * Priority:
   *   1. middleauth+ layer URL auto-extraction (production)
   *   2. EYEWIRE_II_CAVE_CONFIG.caveServerByDataset keyed by layer name (dev)
   *   3. EYEWIRE_II_CAVE_CONFIG.caveServerOverride (last resort)
   */
  function getCaveServerUrl(): string {
    if (!viewer) return EYEWIRE_II_CAVE_CONFIG.caveServerOverride;

    // 1. Try to extract from a middleauth-wrapped datasource URL
    for (const ml of viewer.layerManager.managedLayers) {
      const url = ml.layer?.dataSources?.[0]?.spec?.url ?? '';
      if (url.includes('middleauth')) {
        const clean = url.replace('graphene://middleauth+', '');
        try {
          const u = new URL(clean);
          return `${u.protocol}//${u.host}${u.pathname.split('/segmentation')[0]}`;
        } catch { /* ignore */ }
      }
    }

    // 2. Fall back to per-dataset config
    for (const ml of viewer.layerManager.managedLayers) {
      const layerName = ml.name ?? '';
      const fromConfig = EYEWIRE_II_CAVE_CONFIG.caveServerByDataset?.[layerName];
      if (fromConfig) return fromConfig;
    }

    // 3. Global override
    return EYEWIRE_II_CAVE_CONFIG.caveServerOverride;
  }

  return {initializeWithViewer, activeLayers, selectLayers, getCaveServerUrl};
});

// ─── User Stats Store ────────────────────────────────────────────────────────
// Populated by the external user-profile repo via setStats().
// Call `useUserStatsStore().setStats({...})` from the profile integration to
// wire in live edit counts and cells-submitted totals.

export interface UserStats {
  editsToday: number;
  mergesToday: number;
  splitsToday: number;
  editsThisWeek: number;
  mergesThisWeek: number;
  splitsThisWeek: number;
  editsAllTime: number;
  mergesAllTime: number;
  splitsAllTime: number;
  cellsSubmitted: number;
  // Monthly stats
  editsThisMonth: number;
  mergesThisMonth: number;
  splitsThisMonth: number;
  // Streak — consecutive calendar days with ≥1 edit (merge OR split counts)
  currentStreak: number;
  longestStreak: number;
  lastEditDate: string;       // ISO date string e.g. "2026-03-01"
  // Community totals — dataset-wide aggregate from CAVE ChunkedGraph
  communityEditsThisWeek: number;
  communityEditsThisMonth: number;
}

const STATS_KEY = 'nge_stats_v1';
const DAILY_LOG_KEY = 'nge_daily_log_v1';

export interface DailyLogEntry {
  date: string;          // ISO date "2026-03-04"
  edits: number;
  merges: number;
  splits: number;
  cellsCompleted: number;
  questsCompleted: number;
}

export const useUserStatsStore = defineStore('userStats', () => {
  // Hydrate from localStorage so stats survive page refresh
  const saved = (() => {
    try {
      const raw = localStorage.getItem(STATS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const stats: Ref<UserStats> = ref({
    editsToday: 0,
    mergesToday: 0,
    splitsToday: 0,
    editsThisWeek: 0,
    mergesThisWeek: 0,
    splitsThisWeek: 0,
    editsAllTime: 0,
    mergesAllTime: 0,
    splitsAllTime: 0,
    cellsSubmitted: 0,
    editsThisMonth: 0,
    mergesThisMonth: 0,
    splitsThisMonth: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastEditDate: '',
    communityEditsThisWeek: 0,
    communityEditsThisMonth: 0,
    ...saved,
  });

  function persist() {
    try { localStorage.setItem(STATS_KEY, JSON.stringify(stats.value)); } catch {}
  }

  function setStats(partial: Partial<UserStats>) {
    Object.assign(stats.value, partial);
    // Guard: allTime must never be less than today/week/month
    const s = stats.value;
    s.editsAllTime   = Math.max(s.editsAllTime,   s.editsToday,  s.editsThisWeek,  s.editsThisMonth);
    s.mergesAllTime  = Math.max(s.mergesAllTime,  s.mergesToday, s.mergesThisWeek, s.mergesThisMonth);
    s.splitsAllTime  = Math.max(s.splitsAllTime,  s.splitsToday, s.splitsThisWeek, s.splitsThisMonth);
    s.editsThisWeek  = Math.max(s.editsThisWeek,  s.editsToday);
    s.editsThisMonth = Math.max(s.editsThisMonth, s.editsThisWeek);
    s.mergesThisWeek = Math.max(s.mergesThisWeek, s.mergesToday);
    s.mergesThisMonth= Math.max(s.mergesThisMonth,s.mergesThisWeek);
    s.splitsThisWeek = Math.max(s.splitsThisWeek, s.splitsToday);
    s.splitsThisMonth= Math.max(s.splitsThisMonth,s.splitsThisWeek);
    persist();
  }

  // ── Daily activity log (last 30 days) ───────────────────────────
  const dailyLog = ref<DailyLogEntry[]>([]);

  function loadDailyLog() {
    try {
      const raw = localStorage.getItem(DAILY_LOG_KEY);
      if (raw) dailyLog.value = JSON.parse(raw);
    } catch {}
  }

  function persistDailyLog() {
    try {
      // Keep only last 30 days
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const cutoffStr = cutoff.toISOString().slice(0, 10);
      dailyLog.value = dailyLog.value.filter(d => d.date >= cutoffStr);
      localStorage.setItem(DAILY_LOG_KEY, JSON.stringify(dailyLog.value));
    } catch {}
  }

  function getTodayLog(): DailyLogEntry {
    const today = new Date().toISOString().slice(0, 10);
    let entry = dailyLog.value.find(d => d.date === today);
    if (!entry) {
      entry = { date: today, edits: 0, merges: 0, splits: 0, cellsCompleted: 0, questsCompleted: 0 };
      dailyLog.value.push(entry);
    }
    return entry;
  }

  function logDailyEdit(type: 'merge' | 'split') {
    const entry = getTodayLog();
    entry.edits++;
    if (type === 'merge') entry.merges++;
    else entry.splits++;
    persistDailyLog();
  }

  function logDailyCellComplete() {
    const entry = getTodayLog();
    entry.cellsCompleted++;
    persistDailyLog();
  }

  function logDailyQuestComplete() {
    const entry = getTodayLog();
    entry.questsCompleted++;
    persistDailyLog();
  }

  loadDailyLog();

  // ── Edit event signal for UI celebrations ───────────────────────
  /** Brief reactive signal: set on merge/split, auto-clears after 2.5s. */
  const recentEditEvent = ref<{type: 'merge' | 'split'; ts: number} | null>(null);
  let editEventTimer: ReturnType<typeof setTimeout> | null = null;

  function signalEdit(type: 'merge' | 'split') {
    recentEditEvent.value = {type, ts: Date.now()};
    if (editEventTimer) clearTimeout(editEventTimer);
    editEventTimer = setTimeout(() => { recentEditEvent.value = null; }, 2500);
  }

  return {stats, setStats, dailyLog, logDailyEdit, logDailyCellComplete, logDailyQuestComplete, recentEditEvent, signalEdit};
});

// ─── User Preferences Store ───────────────────────────────────────────────────
// Persists flag emoji + bio to localStorage so they survive page reloads.

const PREFS_KEY = 'nge_prefs_v1';

export interface UserPreferences {
  flag: string;   // flag emoji e.g. "🇺🇸"
  bio: string;    // free-text, capped at 280 chars in the UI
  /** Which toolbar icons to show, in order. Empty = show all defaults. */
  toolbarIcons: string[];
}

export const useUserPreferencesStore = defineStore('userPrefs', () => {
  const prefs: Ref<UserPreferences> = ref({ flag: '', bio: '', toolbarIcons: [] });

  function load() {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      if (raw) Object.assign(prefs.value, JSON.parse(raw));
    } catch { /* ignore parse errors */ }
  }

  function save(partial: Partial<UserPreferences>) {
    Object.assign(prefs.value, partial);
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
  }

  load(); // hydrate from localStorage on store init
  return { prefs, save };
});

// ─── Segment Annotation Store ─────────────────────────────────────────────────
// Tracks the currently-selected segment ID and its CAVE annotation status.
// Populated by main.ts (DOM observer) when a segment list entry appears.

export interface SegmentAnnotation {
  segId: string;
  isComplete: boolean;
  cellType: string;
  annotationId?: number;
  cellTypeAnnotationId?: number;
  loading: boolean;
  error: string;
}

export const useSegmentAnnotationStore = defineStore('segAnnotation', () => {
  const activeSegId  = ref<string | null>(null);
  const caveUrl      = ref<string>('');
  const annotation   = ref<SegmentAnnotation | null>(null);

  function setActiveSegId(id: string | null, cave?: string) {
    activeSegId.value = id;
    if (cave !== undefined) caveUrl.value = cave;
    if (id === null) { annotation.value = null; return; }
    // Reset to loading state so the panel shows a spinner
    annotation.value = {
      segId: id, isComplete: false, cellType: '',
      loading: true, error: '',
    };
  }

  function setAnnotation(a: SegmentAnnotation | null) {
    annotation.value = a;
  }

  return { activeSegId, caveUrl, annotation, setActiveSegId, setAnnotation };
});

// ─── Cell History Store ──────────────────────────────────────────────────────
// Tracks every cell the user has completed, ID'd, or interacted with.
// Persists to localStorage so it survives page reloads.

const CELL_HISTORY_KEY = 'nge_cell_history_v1';

export interface CellHistoryEntry {
  segId: string;
  isComplete: boolean;
  cellType: string;
  /** Viewer position at time of annotation — used for jump-to-cell. */
  position: [number, number, number];
  /** ISO timestamp of last update. */
  updatedAt: string;
  /** User-assigned nickname (displayed instead of segId when set). */
  nickname?: string;
  /** Favorite flag — favorite cells appear at the top of the list. */
  isFavorite?: boolean;
  /** Dataset/layer name this cell belongs to (for filtering). */
  dataset?: string;
}

export const useCellHistoryStore = defineStore('cellHistory', () => {
  const cells = ref<CellHistoryEntry[]>([]);

  function load() {
    try {
      const raw = localStorage.getItem(CELL_HISTORY_KEY);
      if (raw) cells.value = JSON.parse(raw);
    } catch { /* ignore parse errors */ }
  }

  function persist() {
    localStorage.setItem(CELL_HISTORY_KEY, JSON.stringify(cells.value));
  }

  /** Add or update a cell entry. */
  function upsert(entry: Partial<CellHistoryEntry> & { segId: string }) {
    const idx = cells.value.findIndex(c => c.segId === entry.segId);
    const now = new Date().toISOString();
    if (idx >= 0) {
      // Merge fields — don't overwrite existing data with empty values
      const existing = cells.value[idx];
      cells.value[idx] = {
        ...existing,
        ...entry,
        cellType: entry.cellType || existing.cellType,
        position: entry.position || existing.position,
        dataset: entry.dataset || existing.dataset,
        updatedAt: now,
      };
    } else {
      cells.value.unshift({
        segId: entry.segId,
        isComplete: entry.isComplete ?? false,
        cellType: entry.cellType ?? '',
        position: entry.position ?? [0, 0, 0],
        updatedAt: now,
        nickname: entry.nickname,
        isFavorite: entry.isFavorite,
        dataset: entry.dataset,
      });
    }
    // Sort: favorites first, then by most recent
    cells.value.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    persist();
  }

  /** Toggle favorite status for a cell. */
  function toggleFavorite(segId: string) {
    const cell = cells.value.find(c => c.segId === segId);
    if (cell) {
      cell.isFavorite = !cell.isFavorite;
      // Re-sort
      cells.value.sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return b.updatedAt.localeCompare(a.updatedAt);
      });
      persist();
    }
  }

  /** Set a nickname for a cell. */
  function setNickname(segId: string, name: string) {
    const cell = cells.value.find(c => c.segId === segId);
    if (cell) {
      cell.nickname = name.trim() || undefined;
      persist();
    }
  }

  /** Lookup a nickname by segment ID (used globally to replace segID display). */
  function getNickname(segId: string): string | undefined {
    const cell = cells.value.find(c => c.segId === segId);
    return cell?.nickname;
  }

  /** Navigate the viewer to a cell and select it.
   *  If `positionOverride` is given, jump there instead of the cell history position. */
  function jumpToCell(segId: string, positionOverride?: [number, number, number]) {
    const entry = cells.value.find(c => c.segId === segId);
    const viewer: any = (window as any)['viewer'];
    if (!viewer) return;

    // Use override position (e.g. from help request) or fall back to cell history
    const pos = positionOverride ?? entry?.position;
    const hasPosition = pos && (pos[0] || pos[1] || pos[2]);
    if (hasPosition) {
      try {
        viewer.navigationState.position.value = Float32Array.from(pos);
      } catch (e) {
        console.warn('[cellHistory] Could not set position:', e);
      }
    }

    // Select the segment in the first segmentation layer, loading it if needed
    try {
      const segLayer = viewer.layerManager.managedLayers.find(
        (x: any) => x.layer instanceof SegmentationUserLayer,
      );
      if (segLayer?.layer) {
        addSegmentToLayer(segLayer.layer, segId);

        // Also ensure this is the selected layer so the user can see it
        try {
          viewer.selectedLayer.layer = segLayer;
          viewer.selectedLayer.visible = true;
        } catch { /* non-critical */ }

        // If no position was provided, try to navigate to the segment's location
        if (!hasPosition) {
          console.info('[cellHistory] No position for segment', segId, '— attempting auto-navigate');
          // Strategy: click the segment entry in NG's segment list to trigger NG's
          // built-in "zoom to segment" behavior (with a delay for DOM to update)
          setTimeout(() => {
            try {
              const entry = document.querySelector(
                `.neuroglancer-segment-list-entry[data-id="${segId}"]`
              );
              if (entry) {
                // Double-click the segment ID text to trigger NG's zoom-to-segment
                const idEl = entry.querySelector('.neuroglancer-segment-list-entry-id');
                if (idEl) {
                  (idEl as HTMLElement).dispatchEvent(new MouseEvent('dblclick', {
                    bubbles: true, cancelable: true
                  }));
                  console.info('[cellHistory] Double-clicked segment entry to navigate');
                }
              } else {
                console.info('[cellHistory] Segment entry not in DOM yet, segment may still be loading');
              }
            } catch (e) {
              console.warn('[cellHistory] Auto-navigate fallback failed:', e);
            }
          }, 1500); // wait for segment to appear in the list
        }
      } else {
        console.warn('[cellHistory] No segmentation layer found — cannot load segment', segId);
      }
    } catch (e) {
      console.warn('[cellHistory] Could not select segment:', e);
    }
  }


  /**
   * Add a segment to a segmentation layer's visible segments.
   * Reusable utility shared with Brain Quest navigation.
   */
  function addSegmentToLayer(layer: any, segId: string): boolean {
    try {
      const groupState = layer.displayState?.segmentationGroupState?.value;
      if (!groupState) {
        console.warn('[cellHistory] No segmentationGroupState on layer');
        return false;
      }
      // Try multiple ways to get the Uint64 constructor
      let Uint64 = groupState.visibleSegments.hashTable?.emptyValue?.constructor;
      if (!Uint64) {
        // Fallback: try to import from neuroglancer directly
        const iter = groupState.visibleSegments[Symbol.iterator]?.();
        if (iter) {
          const first = iter.next()?.value;
          if (first) Uint64 = first.constructor;
        }
      }
      if (!Uint64?.parseString) {
        console.warn('[cellHistory] Could not obtain Uint64 constructor');
        return false;
      }
      const seg = Uint64.parseString(segId);
      if (!groupState.visibleSegments.has(seg)) {
        console.info('[cellHistory] Adding segment to visible segments:', segId);
        groupState.visibleSegments.add(seg);
      } else {
        console.info('[cellHistory] Segment already visible:', segId);
      }
      return true;
    } catch (e) {
      console.warn('[cellHistory] Failed to add segment:', segId, e);
      return false;
    }
  }

  load(); // hydrate on store init

  // Seed with demo data if empty (real minnie65 segIDs)
  if (cells.value.length === 0) {
    const now = Date.now();
    const SEED: Array<Partial<CellHistoryEntry> & { segId: string }> = [
      { segId: '864691135445639570', isComplete: true, cellType: 'L2/3 Pyramidal',
        position: [120320, 103936, 21360], isFavorite: true },
      { segId: '864691135158265390', isComplete: true, cellType: 'L2/3 Pyramidal',
        position: [119040, 104448, 21280] },
      { segId: '864691135697404831', isComplete: false, cellType: 'L2/3 Pyramidal',
        position: [121600, 102912, 21440] },
      { segId: '864691136239802390', isComplete: true, cellType: 'L4 Spiny Stellate',
        position: [118784, 105984, 21200] },
      { segId: '864691135213953920', isComplete: false, cellType: 'L5 Thick-Tufted',
        position: [120064, 106496, 21120] },
      { segId: '864691135158296972', isComplete: true, cellType: 'L5 Slender-Tufted',
        position: [119808, 103168, 21520] },
      { segId: '864691134884741468', isComplete: false, cellType: '',
        position: [121856, 105472, 21040] },
      { segId: '864691135697405836', isComplete: true, cellType: 'PV+ Basket Cell',
        position: [118528, 104960, 21360], isFavorite: true },
      { segId: '864691135158267390', isComplete: false, cellType: 'SST+ Martinotti',
        position: [120576, 102400, 21600] },
      { segId: '864691136239803118', isComplete: true, cellType: 'VIP+ Bipolar',
        position: [119296, 105216, 21480] },
      { segId: '864691135158265120', isComplete: false, cellType: '',
        position: [121344, 104192, 21240] },
    ];
    SEED.forEach((s, i) => {
      cells.value.push({
        segId: s.segId,
        isComplete: s.isComplete ?? false,
        cellType: s.cellType ?? '',
        position: s.position ?? [0, 0, 0],
        updatedAt: new Date(now - i * 3600000 * 3).toISOString(),
        nickname: s.nickname,
        isFavorite: s.isFavorite,
      });
    });
    cells.value.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    persist();
  }

  return { cells, upsert, jumpToCell, addSegmentToLayer, toggleFavorite, setNickname, getNickname };
});

// ── Help requests (second-opinion) ────────────────────────────────────────────
const HELP_REQUESTS_KEY = 'nge_help_requests_v1';

export interface HelpRequest {
  id: string;
  segId: string;
  position: [number, number, number];
  note: string;
  /** Issue category: extension, merge, black spill, doublecheck */
  issueType: string;
  createdAt: string;
  resolved: boolean;
  /** Cell type guess at time of request */
  cellType?: string;
  nickname?: string;
  /** Dataset/layer name this request belongs to (for cross-dataset filtering). */
  dataset?: string;
}

export const useHelpRequestStore = defineStore('helpRequests', () => {
  const requests = ref<HelpRequest[]>([]);

  function load() {
    try {
      const raw = localStorage.getItem(HELP_REQUESTS_KEY);
      if (raw) requests.value = JSON.parse(raw);
    } catch {}
  }

  function persist() {
    localStorage.setItem(HELP_REQUESTS_KEY, JSON.stringify(requests.value));
  }

  function add(req: Omit<HelpRequest, 'id' | 'createdAt' | 'resolved'>) {
    requests.value.unshift({
      ...req,
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      createdAt: new Date().toISOString(),
      resolved: false,
    });
    persist();
  }

  function resolve(id: string) {
    const r = requests.value.find(x => x.id === id);
    if (r) { r.resolved = true; persist(); }
  }

  function remove(id: string) {
    requests.value = requests.value.filter(x => x.id !== id);
    persist();
  }

  const pending = ref<HelpRequest[]>([]);
  // Keep a reactive computed-like ref
  function refreshPending() {
    pending.value = requests.value.filter(x => !x.resolved);
  }

  load();
  refreshPending();
  return { requests, pending, add, resolve, remove, refreshPending };
});

// ── Proofreading Queue ─────────────────────────────────────────────────────
// Loads a queue of segments to review from a published Google Sheet (CSV).
// Users cycle through items, mark them reviewed, and track session progress.

const QUEUE_SHEET_KEY    = 'nge_queue_sheet_url_v1';
const QUEUE_SHEET_VER_KEY = 'nge_queue_sheet_ver';
const QUEUE_SHEET_VERSION = 2;  // Bump when DEFAULT_QUEUE_SHEET changes to invalidate cached URLs
const DEFAULT_QUEUE_SHEET = 'https://docs.google.com/spreadsheets/d/1SdepJzadXMz5TC-5DFZxUyDJk7efEPP39HE0hmUAJjU/edit?gid=0#gid=0';

/** Hash a sheet URL into a short suffix so each sheet gets its own storage keys. */
function sheetKeyHash(url: string): string {
  if (!url) return '_default';
  let h = 0;
  for (let i = 0; i < url.length; i++) h = ((h << 5) - h + url.charCodeAt(i)) | 0;
  return '_' + Math.abs(h).toString(36);
}

export interface QueueItem {
  segId: string;
  index: string;              // e.g. "B1", "B2"
  nucCoords: string;          // nucleus coordinates "x, y, z"
  somaCoords: string;         // soma coordinates "x, y, z"
  finalSegId: string;         // final segment ID after proofreading
  finalNucId: string;         // final nucleus ID
  notes: string;
}

export const useProofreadingQueueStore = defineStore('proofreadingQueue', () => {
  const items       = ref<QueueItem[]>([]);
  const currentIdx  = ref(0);
  const proofread   = ref<Set<string>>(new Set());   // formerly "reviewed"
  const loading     = ref(false);
  const error       = ref('');
  const sheetUrl    = ref('');
  const sessionStart = Date.now();

  // Per-item local edits (soma coords, final seg id, annotation) keyed by segId
  const localEdits = ref<Record<string, { somaCoords?: string; finalSegId?: string; annotation?: string }>>(
    {});

  // Segment lineage: tracks new segment IDs created by splits/merges while
  // the user works on a quest item.  Maps originalSegId → [derivedSegId, ...]
  const segmentLineage = ref<Record<string, string[]>>({});

  // Persist proofread set, sheet URL, and local edits — keyed per-sheet
  // so different datasets (pinky vs minnie65) don't clobber each other.
  function reviewedKey() { return 'nge_queue_reviewed' + sheetKeyHash(sheetUrl.value); }
  function editsKey()    { return 'nge_queue_edits' + sheetKeyHash(sheetUrl.value); }
  function lineageKey()  { return 'nge_queue_lineage' + sheetKeyHash(sheetUrl.value); }

  function loadLocal() {
    try {
      // Check if the default sheet changed (version bump invalidates cached URL)
      const savedVer = parseInt(localStorage.getItem(QUEUE_SHEET_VER_KEY) || '0', 10);
      if (savedVer < QUEUE_SHEET_VERSION) {
        // Default sheet updated — reset to new default, clear stale cache
        sheetUrl.value = DEFAULT_QUEUE_SHEET;
        localStorage.removeItem(QUEUE_SHEET_KEY);
        localStorage.setItem(QUEUE_SHEET_VER_KEY, String(QUEUE_SHEET_VERSION));
        console.info('[queue] Default sheet updated (v' + QUEUE_SHEET_VERSION + '), cleared cached URL');
      } else {
        const url = localStorage.getItem(QUEUE_SHEET_KEY);
        sheetUrl.value = url || DEFAULT_QUEUE_SHEET;
      }
    } catch { sheetUrl.value = DEFAULT_QUEUE_SHEET; }
    try {
      const raw = localStorage.getItem(reviewedKey());
      if (raw) proofread.value = new Set(JSON.parse(raw));
    } catch {}
    try {
      const edits = localStorage.getItem(editsKey());
      if (edits) localEdits.value = JSON.parse(edits);
    } catch {}
    try {
      const lin = localStorage.getItem(lineageKey());
      if (lin) segmentLineage.value = JSON.parse(lin);
    } catch {}
  }

  function persistProofread() {
    localStorage.setItem(reviewedKey(), JSON.stringify([...proofread.value]));
  }

  function persistEdits() {
    localStorage.setItem(editsKey(), JSON.stringify(localEdits.value));
  }

  function persistLineage() {
    localStorage.setItem(lineageKey(), JSON.stringify(segmentLineage.value));
  }

  function persistSheetUrl() {
    localStorage.setItem(QUEUE_SHEET_KEY, sheetUrl.value);
    localStorage.setItem(QUEUE_SHEET_VER_KEY, String(QUEUE_SHEET_VERSION));
  }

  /** Get local edits for a segment, merged with sheet data. */
  function getEdits(segId: string) {
    return localEdits.value[segId] || {};
  }

  /** Save local edit for a specific field. */
  function setEdit(segId: string, field: 'somaCoords' | 'finalSegId' | 'annotation', value: string) {
    if (!localEdits.value[segId]) localEdits.value[segId] = {};
    localEdits.value[segId][field] = value;
    persistEdits();
  }

  /** Check if item has soma coords from local edits — i.e. "claimed" by this user. */
  function isClaimed(item: QueueItem): boolean {
    const edits = getEdits(item.segId);
    return (edits.somaCoords || '').trim().length > 0;
  }

  /** Check if item can be marked proofread (has soma + final seg id from local edits). */
  function canComplete(item: QueueItem): boolean {
    const edits = getEdits(item.segId);
    const hasSoma  = (edits.somaCoords || '').trim().length > 0;
    const hasFinal = (edits.finalSegId || '').trim().length > 0;
    return hasSoma && hasFinal;
  }

  /** Parse CSV text into rows (handles quoted fields). */
  function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { field += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { row.push(field.trim()); field = ''; }
        else if (ch === '\n' || ch === '\r') {
          if (ch === '\r' && text[i + 1] === '\n') i++;
          row.push(field.trim()); field = '';
          if (row.some(f => f)) rows.push(row);
          row = [];
        } else { field += ch; }
      }
    }
    row.push(field.trim());
    if (row.some(f => f)) rows.push(row);
    return rows;
  }

  /**
   * Fetch and parse a published Google Sheet CSV.
   * Finds the header row dynamically (looks for "Segment ID" in any row).
   * Expected columns: Index, Nuc Coords, Soma Coords, Segment ID,
   *                   Final SegID, Final NucID, Notes
   */
  async function loadFromSheet(url?: string) {
    const sheetSource = url || sheetUrl.value;
    if (!sheetSource) { error.value = 'No sheet URL configured'; return; }

    loading.value = true;
    error.value = '';

    try {
      // Convert share URL to CSV export URL
      let csvUrl = sheetSource;
      const match = sheetSource.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match) {
        // Extract gid if present (for multi-tab sheets)
        const gidMatch = sheetSource.match(/gid=(\d+)/);
        const gid = gidMatch ? gidMatch[1] : '0';
        csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv&gid=${gid}`;
      }

      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { error.value = 'Sheet is empty'; loading.value = false; return; }

      // Find header row dynamically — look for a row containing "segment"
      let headerIdx = 0;
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const lower = rows[i].map(c => c.toLowerCase());
        if (lower.some(c => c.includes('segment'))) { headerIdx = i; break; }
      }

      const header = rows[headerIdx].map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const col = (name: string) =>
        header.findIndex(h => h.includes(name));

      const iIndex    = col('index');
      const iNuc      = col('nuccoord') >= 0 ? col('nuccoord') : col('nuc');
      const iSoma     = col('somacoord') >= 0 ? col('somacoord') : col('soma');
      const iSeg      = col('segmentid') >= 0 ? col('segmentid') : col('segment');
      const iFinal    = col('finalseg');
      const iFinalNuc = col('finalnuc');
      const iNotes    = col('note');

      if (iSeg < 0) { error.value = 'Could not find "Segment ID" column'; loading.value = false; return; }

      const parsed: QueueItem[] = [];
      for (let r = headerIdx + 1; r < rows.length; r++) {
        const row = rows[r];
        const segId = (row[iSeg] || '').trim();
        if (!segId || !/^\d+$/.test(segId)) continue;

        parsed.push({
          segId,
          index:      (iIndex >= 0 ? row[iIndex] : '') || '',
          nucCoords:  cleanCoord(iNuc >= 0 ? row[iNuc] : ''),
          somaCoords: cleanCoord(iSoma >= 0 ? row[iSoma] : ''),
          finalSegId: (iFinal >= 0 ? row[iFinal] : '') || '',
          finalNucId: (iFinalNuc >= 0 ? row[iFinalNuc] : '') || '',
          notes:      (iNotes >= 0 ? row[iNotes] : '') || '',
        });
      }

      items.value = parsed;
      if (url && url !== sheetUrl.value) {
        // Switching to a different sheet — save current, then load new sheet's data
        sheetUrl.value = url;
        persistSheetUrl();
        // Load proofread/edits for the NEW sheet (keyed by URL hash)
        try {
          const raw = localStorage.getItem(reviewedKey());
          proofread.value = raw ? new Set(JSON.parse(raw)) : new Set();
        } catch { proofread.value = new Set(); }
        try {
          const edits = localStorage.getItem(editsKey());
          localEdits.value = edits ? JSON.parse(edits) : {};
        } catch { localEdits.value = {}; }
        currentIdx.value = 0;
      }
    } catch (e: any) {
      error.value = e.message || 'Failed to load sheet';
    }
    loading.value = false;
  }

  /** Clean a coordinate string — strip dashes and whitespace-only values. */
  function cleanCoord(s: string): string {
    const trimmed = s.trim();
    return (trimmed === '-' || trimmed === '') ? '' : trimmed;
  }

  // ── Navigation ──────────────────────────────────────────────────────────
  function currentItem(): QueueItem | null {
    return items.value[currentIdx.value] ?? null;
  }

  function next() {
    if (items.value.length === 0) return;
    currentIdx.value = (currentIdx.value + 1) % items.value.length;
    navigateToCurrentItem();
  }

  function prev() {
    if (items.value.length === 0) return;
    currentIdx.value = (currentIdx.value - 1 + items.value.length) % items.value.length;
    navigateToCurrentItem();
  }

  function jumpToIndex(idx: number) {
    if (idx >= 0 && idx < items.value.length) {
      currentIdx.value = idx;
      navigateToCurrentItem();
    }
  }

  function nextUnproofread() {
    if (items.value.length === 0) return;
    for (let i = 1; i <= items.value.length; i++) {
      const idx = (currentIdx.value + i) % items.value.length;
      if (!proofread.value.has(items.value[idx].segId)) {
        currentIdx.value = idx;
        navigateToCurrentItem();
        return;
      }
    }
  }

  function navigateToCurrentItem() {
    const item = currentItem();
    if (!item) return;
    const edits = getEdits(item.segId);
    // Prefer local edits soma, then nuc coords from sheet (for initial navigation)
    const coordStr = edits.somaCoords || item.nucCoords || '';
    const pos = parseCoordString(coordStr);
    const historyStore = useCellHistoryStore();
    // Always jump to segment (selects it), position is optional
    historyStore.jumpToCell(item.segId, pos[0] || pos[1] || pos[2] ? pos : undefined);

    // Also load any derived segments from lineage (splits/merges during proofreading)
    const derived = segmentLineage.value[item.segId];
    if (derived && derived.length > 0) {
      const viewer: any = (window as any)['viewer'];
      if (viewer) {
        const segLayer = viewer.layerManager?.managedLayers?.find(
          (x: any) => x.layer instanceof SegmentationUserLayer,
        );
        if (segLayer?.layer) {
          for (const derivedId of derived) {
            historyStore.addSegmentToLayer(segLayer.layer, derivedId);
          }
        }
      }
    }
  }

  /**
   * Called by the segment edit watcher when segments change.
   * Associates new segment IDs with the current quest item.
   */
  function onSegmentEdit(removedIds: string[], addedIds: string[]) {
    const item = currentItem();
    if (!item) return;

    const origId = item.segId;
    // Get or create lineage set for this quest item
    if (!segmentLineage.value[origId]) {
      segmentLineage.value[origId] = [];
    }
    const lineage = segmentLineage.value[origId];
    const lineageSet = new Set(lineage);

    // All IDs we're tracking for this quest item (original + all derived)
    const trackedIds = new Set([origId, ...lineage]);

    // Check if any removed segments are ones we're tracking
    const relevantRemoved = removedIds.filter(id => trackedIds.has(id));
    if (relevantRemoved.length === 0 && addedIds.length === 0) return;

    // If tracked segments were removed and new ones were added,
    // associate the new ones with this quest item
    if (relevantRemoved.length > 0 && addedIds.length > 0) {
      for (const newId of addedIds) {
        if (newId !== origId && !lineageSet.has(newId)) {
          lineage.push(newId);
          lineageSet.add(newId);
          console.info(`[quest-lineage] ${origId}: added derived segment ${newId}`);
        }
      }
      // Remove consumed segments from lineage (they no longer exist)
      for (const removedId of relevantRemoved) {
        if (removedId !== origId) {
          const idx = lineage.indexOf(removedId);
          if (idx >= 0) lineage.splice(idx, 1);
        }
      }
      persistLineage();
    }
  }

  /** Get all segment IDs associated with a quest item (original + derived). */
  function getLineage(segId: string): string[] {
    return [segId, ...(segmentLineage.value[segId] || [])];
  }

  /** Parse "x, y, z" coordinate string into a tuple. */
  function parseCoordString(s: string): [number, number, number] {
    if (!s) return [0, 0, 0];
    const parts = s.split(',').map(p => parseInt(p.trim(), 10) || 0);
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  }

  function markProofread(segId?: string) {
    const id = segId || currentItem()?.segId;
    if (id) { proofread.value.add(id); persistProofread(); }
  }

  function unmarkProofread(segId: string) {
    proofread.value.delete(segId);
    persistProofread();
  }

  function clearProofread() {
    proofread.value.clear();
    persistProofread();
  }

  /** Clear all local edits (soma coords, final seg ID, annotations). */
  function clearLocalEdits() {
    localEdits.value = {};
    persistEdits();
  }

  /** Full reset: clear proofread set + local edits, jump to start. */
  function resetAll() {
    proofread.value.clear();
    localEdits.value = {};
    persistProofread();
    persistEdits();
    currentIdx.value = 0;
  }

  // ── Google Sheets write-back ─────────────────────────────────────────

  /**
   * Write soma coordinates back to the Google Sheet for the given segment.
   * Uses Google Sheets API v4 with an API key (sheet must be publicly editable,
   * or the user must be signed into Google in this browser).
   */
  async function writeSomaCoordsToSheet(segId: string, coords: string) {
    const source = sheetUrl.value;
    if (!source) return;

    // Extract spreadsheet ID
    const idMatch = source.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!idMatch) { console.warn('[quest] Cannot extract spreadsheet ID from URL'); return; }
    const spreadsheetId = idMatch[1];
    const gidMatch = source.match(/gid=(\d+)/);
    const gid = gidMatch ? gidMatch[1] : '0';

    // Find the row for this segId in our items array
    const item = items.value.find(i => i.segId === segId);
    if (!item) return;
    const itemIdx = items.value.indexOf(item);

    // We need to know the header row offset and soma column.
    // Re-fetch the sheet to find the exact cell reference.
    try {
      const csvUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv&gid=${gid}`;
      const res = await fetch(csvUrl);
      if (!res.ok) { console.warn('[quest] Could not fetch sheet for write-back'); return; }
      const text = await res.text();
      const rows = parseCsv(text);

      // Find header row
      let headerIdx = 0;
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const lower = rows[i].map(c => c.toLowerCase());
        if (lower.some(c => c.includes('segment'))) { headerIdx = i; break; }
      }

      const header = rows[headerIdx].map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const somaColIdx = header.findIndex(h => h.includes('somacoord') || h.includes('soma'));
      if (somaColIdx < 0) { console.warn('[quest] No "Soma Coords" column found'); return; }

      // Convert column index to letter (A, B, C, ... Z, AA, AB, etc.)
      const colLetter = (idx: number) => {
        let s = '';
        let n = idx;
        while (n >= 0) { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; }
        return s;
      };

      // The data row is: headerIdx + 1 + itemIdx (0-based data rows)
      // In the sheet, row numbers are 1-based
      const sheetRow = headerIdx + 1 + itemIdx + 1; // +1 for header, +1 for 1-based
      const cellRef = `${colLetter(somaColIdx)}${sheetRow}`;

      // Try writing via Google Sheets API v4 (requires the sheet to be editable)
      const apiUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${cellRef}?valueInputOption=USER_ENTERED`;
      const writeRes = await fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          range: cellRef,
          majorDimension: 'ROWS',
          values: [[coords]],
        }),
      });

      if (writeRes.ok) {
        console.info(`[quest] ✓ Soma coords written to sheet cell ${cellRef}`);
      } else {
        const errText = await writeRes.text().catch(() => '');
        console.warn(`[quest] Sheet write failed (${writeRes.status}): ${errText}`);
        console.info('[quest] Soma coords saved locally. To enable Google Sheets write-back, share the sheet with "anyone with the link can edit" and add a Google API key.');
      }
    } catch (e) {
      console.warn('[quest] Sheet write-back error:', e);
    }
  }

  // ── Computed-like helpers ───────────────────────────────────────────────
  function proofreadCount(): number { return proofread.value.size; }
  function totalCount(): number { return items.value.length; }
  function pendingCount(): number {
    return items.value.filter(i => !proofread.value.has(i.segId)).length;
  }
  function sessionMinutes(): number {
    return Math.floor((Date.now() - sessionStart) / 60000);
  }

  loadLocal();
  // Auto-load if we have a saved sheet URL
  if (sheetUrl.value) loadFromSheet();

  return {
    items, currentIdx, proofread, loading, error, sheetUrl, localEdits, segmentLineage,
    loadFromSheet, currentItem, next, prev, jumpToIndex, nextUnproofread,
    navigateToCurrentItem, markProofread, unmarkProofread, clearProofread, clearLocalEdits, resetAll,
    proofreadCount, totalCount, pendingCount, sessionMinutes,
    getEdits, setEdit, isClaimed, canComplete, writeSomaCoordsToSheet,
    onSegmentEdit, getLineage,
  };
});

/* ───────── Split / Merge Overlay Store ───────── */

export const useSplitMergeOverlayStore = defineStore('splitMergeOverlay', () => {
  const toolActive = ref<'multicut' | 'merge' | null>(null);
  const activeGroup = ref<'red' | 'blue'>('red');
  const redPointCount = ref(0);
  const bluePointCount = ref(0);
  const mergeSubmissionCount = ref(0);
  const submitting = ref(false);
  /** Status message scraped from neuroglancer's tool UI */
  const statusMessage = ref('');
  /** Result flash: 'success' | 'error' | '' */
  const resultFlash = ref<'success' | 'error' | ''>('');
  const resultText = ref('');
  /** When true, bar holds visible in success/error state before closing */
  const pendingClose = ref(false);
  /** Which tool was active before pendingClose (for display) */
  const closingTool = ref<'multicut' | 'merge' | null>(null);
  /**
   * When the user explicitly dismisses (Escape/Cancel), prevent scanToolState
   * from re-opening the overlay until the tool DOM actually goes away and
   * comes back (i.e. user manually re-activates the tool with C/M key).
   */
  const userDismissed = ref(false);
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  function setToolState(tool: 'multicut' | 'merge' | null) {
    if (pendingClose.value) return; // don't interfere during close animation
    // If user dismissed, block re-opening from the poller
    if (userDismissed.value && tool !== null) return;
    if (tool !== toolActive.value) {
      // Clear stale results when mode changes (e.g. split error when entering merge)
      if (tool !== null && toolActive.value !== null && tool !== toolActive.value) {
        clearResults();
      }
      toolActive.value = tool;
      if (tool === null) {
        redPointCount.value = 0;
        bluePointCount.value = 0;
        mergeSubmissionCount.value = 0;
        submitting.value = false;
        statusMessage.value = '';
        clearResults();
        userDismissed.value = false; // reset dismiss when tool fully gone
      }
    }
  }

  /** Clear result/error flash state. */
  function clearResults() {
    if (flashTimer) { clearTimeout(flashTimer); flashTimer = null; }
    resultFlash.value = '';
    resultText.value = '';
    submitting.value = false;
    statusMessage.value = '';
  }

  /** User explicitly dismissed the overlay (Escape or Cancel). */
  function dismiss() {
    userDismissed.value = true;
    pendingClose.value = false;
    toolActive.value = null;
    closingTool.value = null;
    redPointCount.value = 0;
    bluePointCount.value = 0;
    mergeSubmissionCount.value = 0;
    clearResults();
  }

  function setActiveGroup(group: 'red' | 'blue') {
    activeGroup.value = group;
  }

  function updatePointCounts(red: number, blue: number) {
    redPointCount.value = red;
    bluePointCount.value = blue;
  }

  function setStatusMessage(msg: string) {
    statusMessage.value = msg;
  }

  /** Show inline result on the bar (merge tool stays active after) */
  function showResult(type: 'success' | 'error', text: string, durationMs = 4000) {
    resultFlash.value = type;
    resultText.value = text;
    submitting.value = false;
    statusMessage.value = '';
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      resultFlash.value = '';
      resultText.value = '';
    }, durationMs);
  }

  /** Hold bar in success state for 2.5s, then holographic exit */
  function beginSuccessClose(text: string) {
    closingTool.value = toolActive.value;
    pendingClose.value = true;
    resultFlash.value = 'success';
    resultText.value = text;
    submitting.value = false;
    statusMessage.value = '';
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      pendingClose.value = false;
      toolActive.value = null;
      closingTool.value = null;
      resultFlash.value = '';
      resultText.value = '';
      redPointCount.value = 0;
      bluePointCount.value = 0;
      mergeSubmissionCount.value = 0;
    }, 2500);
  }

  return {
    toolActive, activeGroup, redPointCount, bluePointCount,
    mergeSubmissionCount, submitting, statusMessage, resultFlash, resultText,
    pendingClose, closingTool, userDismissed,
    setToolState, setActiveGroup, updatePointCounts, setStatusMessage,
    showResult, beginSuccessClose, dismiss, clearResults,
  };
});

/* ───────── Proofreading Backend Store (Supabase) ───────── */

export interface ProofreadingTask {
  id: number;
  segment_id: string;
  dataset: string;
  nucleus_coords: string | null;
  soma_coords: string | null;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'skipped';
  assigned_to: string | null;
  priority: number;
  final_segment_id: string | null;
  final_nucleus_id: string | null;
  notes: string | null;
  source_sheet_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface EditLogEntry {
  task_id?: number | null;
  user_id?: string | null;
  operation: string;
  segment_before?: string | null;
  segment_after?: string | null;
  coordinates?: string | null;
  metadata?: Record<string, any> | null;
  dataset?: string;
  success?: boolean;
}

export interface ActivityFeedItem {
  id: number;
  user_id: string | null;
  user_name: string | null;
  action: string;
  segment_id: string | null;
  timestamp: string;
}

export const useProofreadingBackendStore = defineStore('proofreadingBackend', () => {
  const userId = ref<string | null>(null);
  const userEmail = ref<string>('');
  const userName = ref<string>('');
  const tasks = ref<ProofreadingTask[]>([]);
  const activeTaskId = ref<number | null>(null);
  const activityFeed = ref<ActivityFeedItem[]>([]);
  const loading = ref(false);
  const error = ref('');

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let feedSubscription: any = null;

  // ── User sync ─────────────────────────────────────────────────────────
  /** Upsert user from middleauth identity. Call after login. */
  async function syncUser(email: string, displayName: string) {
    userEmail.value = email;
    userName.value = displayName;
    console.info('[backend] syncUser called for', email);
    try {
      // Try to find existing user by email
      const { data: existing, error: selectErr } = await supabase
        .from('users')
        .select('id')
        .eq('middleauth_email', email)
        .single();

      if (selectErr && selectErr.code !== 'PGRST116') {
        // PGRST116 = "no rows returned" (expected for new users)
        console.warn('[backend] syncUser SELECT error:', selectErr.message);
      }

      if (existing) {
        userId.value = existing.id;
        console.info('[backend] syncUser: found existing user', existing.id);
        // Update display name if changed
        await supabase
          .from('users')
          .update({ display_name: displayName, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        // Create new user
        console.info('[backend] syncUser: creating new user for', email);
        const { data: newUser, error: insertErr } = await supabase
          .from('users')
          .insert({ middleauth_email: email, display_name: displayName })
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        userId.value = newUser?.id ?? null;
        console.info('[backend] syncUser: created user', userId.value);
      }
    } catch (e: any) {
      console.warn('[backend] User sync failed:', e.message);
    }
  }

  // ── Task management ───────────────────────────────────────────────────
  /** Load tasks for a dataset, with optional status filter. */
  async function loadTasks(dataset: string = 'eyewire_ii', statusFilter?: string) {
    loading.value = true;
    error.value = '';
    try {
      let query = supabase
        .from('proofreading_tasks')
        .select('*')
        .eq('dataset', dataset)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      tasks.value = data ?? [];
    } catch (e: any) {
      error.value = e.message || 'Failed to load tasks';
      console.warn('[backend] loadTasks error:', e.message);
    }
    loading.value = false;
  }

  /** Claim a task — insert assignment, update task status, start heartbeat. */
  async function claimTask(taskId: number) {
    if (!userId.value) { error.value = 'Not logged in'; return false; }
    try {
      // Insert assignment
      const { error: assignErr } = await supabase
        .from('task_assignments')
        .insert({
          task_id: taskId,
          user_id: userId.value,
          expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        });
      if (assignErr) throw assignErr;

      // Update task status
      const { error: taskErr } = await supabase
        .from('proofreading_tasks')
        .update({ status: 'assigned', assigned_to: userId.value, updated_at: new Date().toISOString() })
        .eq('id', taskId);
      if (taskErr) throw taskErr;

      activeTaskId.value = taskId;
      startHeartbeat();

      // Log to edit_log
      await logEdit({ operation: 'claim_task', task_id: taskId });

      // Post to activity feed
      await postActivity(`claimed task #${taskId}`);

      return true;
    } catch (e: any) {
      error.value = e.message || 'Failed to claim task';
      console.warn('[backend] claimTask error:', e.message);
      return false;
    }
  }

  /** Release the currently active task (give up / skip). */
  async function releaseTask() {
    if (!activeTaskId.value || !userId.value) return;
    const taskId = activeTaskId.value;
    try {
      // Mark assignment as released
      await supabase
        .from('task_assignments')
        .update({ status: 'released', released_at: new Date().toISOString() })
        .eq('task_id', taskId)
        .eq('user_id', userId.value)
        .eq('status', 'active');

      // Set task back to pending
      await supabase
        .from('proofreading_tasks')
        .update({ status: 'pending', assigned_to: null, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      await logEdit({ operation: 'release_task', task_id: taskId });

      activeTaskId.value = null;
      stopHeartbeat();
    } catch (e: any) {
      console.warn('[backend] releaseTask error:', e.message);
    }
  }

  /** Mark a task as completed with final segment/soma data. */
  async function completeTask(taskId: number, finalSegId?: string, somaCoords?: string) {
    if (!userId.value) return;
    try {
      const updates: any = {
        status: 'completed',
        updated_at: new Date().toISOString(),
      };
      if (finalSegId) updates.final_segment_id = finalSegId;
      if (somaCoords) updates.soma_coords = somaCoords;

      await supabase
        .from('proofreading_tasks')
        .update(updates)
        .eq('id', taskId);

      // Mark assignment as completed
      await supabase
        .from('task_assignments')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('task_id', taskId)
        .eq('user_id', userId.value)
        .eq('status', 'active');

      await logEdit({
        operation: 'complete_task',
        task_id: taskId,
        metadata: { final_segment_id: finalSegId, soma_coords: somaCoords },
      });

      // Find segment_id for the feed
      const task = tasks.value.find(t => t.id === taskId);
      const segLabel = task?.segment_id ? `...${task.segment_id.slice(-4)}` : `#${taskId}`;
      await postActivity(`completed ${segLabel}`);

      if (activeTaskId.value === taskId) {
        activeTaskId.value = null;
        stopHeartbeat();
      }

      // Update user stats timestamp
      await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', userId.value);
    } catch (e: any) {
      console.warn('[backend] completeTask error:', e.message);
    }
  }

  // ── Heartbeat — extends assignment expiry every 10 min ────────────────
  function startHeartbeat() {
    stopHeartbeat();
    heartbeatInterval = setInterval(async () => {
      if (!activeTaskId.value || !userId.value) return;
      try {
        await supabase
          .from('task_assignments')
          .update({ expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString() })
          .eq('task_id', activeTaskId.value)
          .eq('user_id', userId.value)
          .eq('status', 'active');
      } catch { /* heartbeat failure is non-fatal */ }
    }, 10 * 60 * 1000); // every 10 minutes
  }

  function stopHeartbeat() {
    if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null; }
  }

  // ── Edit logging ──────────────────────────────────────────────────────
  /** Log an edit operation to the audit trail + increment user stats + update streaks. */
  async function logEdit(entry: EditLogEntry) {
    try {
      const uid = entry.user_id ?? userId.value;
      if (!uid) {
        console.warn('[backend] logEdit skipped — no userId set (syncUser may not have run yet)');
      }
      // Fire audit trail insert (non-blocking)
      supabase.from('edit_log').insert({
        task_id: entry.task_id ?? activeTaskId.value,
        user_id: uid,
        operation: entry.operation,
        segment_before: entry.segment_before ?? null,
        segment_after: entry.segment_after ?? null,
        coordinates: entry.coordinates ?? null,
        metadata: entry.metadata ?? null,
        dataset: entry.dataset ?? 'eyewire_ii',
        success: entry.success ?? true,
      }).then(
        ({ error }) => { if (error) console.warn('[backend] edit_log insert failed:', error.message); },
        () => {},
      );

      // Increment user stats in Supabase + calculate streak
      if (!userId.value || !(entry.success ?? true)) return;
      const op = entry.operation;
      const diff = (entry.metadata as any)?.diff ?? 1;

      // Single SELECT to get all stats we need
      const { data: row } = await supabase.from('users')
        .select('total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak, last_edit_date')
        .eq('id', userId.value).single();
      if (!row) return;

      const today = new Date().toISOString().slice(0, 10);
      const updates: Record<string, any> = {
        updated_at: new Date().toISOString(),
        last_edit_date: today,
      };

      // Increment operation-specific counters
      if (op === 'merge') {
        updates.total_edits = (row.total_edits || 0) + diff;
        updates.total_merges = (row.total_merges || 0) + diff;
      } else if (op === 'split') {
        updates.total_edits = (row.total_edits || 0) + diff;
        updates.total_splits = (row.total_splits || 0) + diff;
      } else if (op === 'mark_complete') {
        updates.cells_completed = (row.cells_completed || 0) + 1;
      }

      // Calculate streak: consecutive calendar days with edits
      const lastDate = row.last_edit_date;
      let streak = row.current_streak || 0;
      let longest = row.longest_streak || 0;
      if (lastDate !== today) {
        // Different day — check if it's consecutive
        const lastMs = lastDate ? new Date(lastDate + 'T00:00:00').getTime() : 0;
        const todayMs = new Date(today + 'T00:00:00').getTime();
        const dayGap = Math.round((todayMs - lastMs) / 86400000);
        if (dayGap === 1) {
          streak += 1; // consecutive day
        } else if (dayGap > 1 || !lastDate) {
          streak = 1; // streak broken, restart
        }
        longest = Math.max(longest, streak);
        updates.current_streak = streak;
        updates.longest_streak = longest;
      }

      await supabase.from('users').update(updates).eq('id', userId.value);

      // Immediately update local stats store for instant UI feedback
      const statsStore = useUserStatsStore();
      const localUpdates: Record<string, any> = {};
      if (updates.total_edits !== undefined) localUpdates.editsAllTime = updates.total_edits;
      if (updates.total_merges !== undefined) localUpdates.mergesAllTime = updates.total_merges;
      if (updates.total_splits !== undefined) localUpdates.splitsAllTime = updates.total_splits;
      if (updates.cells_completed !== undefined) localUpdates.cellsSubmitted = updates.cells_completed;
      if (updates.current_streak !== undefined) localUpdates.currentStreak = updates.current_streak;
      if (updates.longest_streak !== undefined) localUpdates.longestStreak = updates.longest_streak;
      localUpdates.lastEditDate = today;
      statsStore.setStats(localUpdates);
    } catch (e: any) {
      console.warn('[backend] logEdit error:', e.message);
    }
  }

  // ── Activity feed ─────────────────────────────────────────────────────
  /** Post an action to the activity feed. */
  async function postActivity(action: string, segmentId?: string) {
    try {
      await supabase.from('activity_feed').insert({
        user_id: userId.value,
        user_name: userName.value || userEmail.value?.split('@')[0] || 'Anonymous',
        action,
        segment_id: segmentId ?? null,
      });
    } catch (e: any) {
      console.warn('[backend] postActivity error:', e.message);
    }
  }

  /** Subscribe to realtime activity feed updates. */
  function subscribeToFeed() {
    if (feedSubscription) return; // already subscribed
    // Load recent feed items
    supabase
      .from('activity_feed')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) activityFeed.value = data.reverse();
      });

    // Subscribe to new inserts
    feedSubscription = supabase
      .channel('activity_feed_realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'activity_feed',
      }, (payload: any) => {
        activityFeed.value.push(payload.new as ActivityFeedItem);
        // Keep only last 100 items in memory
        if (activityFeed.value.length > 100) {
          activityFeed.value = activityFeed.value.slice(-100);
        }
      })
      .subscribe();
  }

  /** Unsubscribe from realtime feed. */
  function unsubscribeFromFeed() {
    if (feedSubscription) {
      supabase.removeChannel(feedSubscription);
      feedSubscription = null;
    }
  }

  // ── Import from Google Sheet (reuses parseCsv from queue store) ─────
  /** Batch-import tasks from a Google Sheet CSV into Supabase. */
  async function importFromGoogleSheet(url: string, dataset: string = 'eyewire_ii') {
    loading.value = true;
    error.value = '';
    try {
      // Convert to CSV export URL
      let csvUrl = url;
      const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match) {
        const gidMatch = url.match(/gid=(\d+)/);
        const gid = gidMatch ? gidMatch[1] : '0';
        csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv&gid=${gid}`;
      }

      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();

      // Parse CSV lines (simple split — Google Sheets CSV export is well-formed)
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) { error.value = 'Sheet is empty'; loading.value = false; return; }

      // Find header
      let headerIdx = 0;
      for (let i = 0; i < Math.min(lines.length, 10); i++) {
        if (lines[i].toLowerCase().includes('segment')) { headerIdx = i; break; }
      }

      const header = lines[headerIdx].split(',').map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const col = (name: string) => header.findIndex(h => h.includes(name));
      const iSeg = col('segmentid') >= 0 ? col('segmentid') : col('segment');
      const iNuc = col('nuccoord') >= 0 ? col('nuccoord') : col('nuc');
      const iSoma = col('somacoord') >= 0 ? col('somacoord') : col('soma');
      const iNotes = col('note');

      if (iSeg < 0) { error.value = 'No "Segment ID" column found'; loading.value = false; return; }

      const toInsert: any[] = [];
      for (let r = headerIdx + 1; r < lines.length; r++) {
        const fields = lines[r].split(',').map(f => f.trim());
        const segId = fields[iSeg] || '';
        if (!segId || !/^\d+$/.test(segId)) continue;

        toInsert.push({
          segment_id: segId,
          dataset,
          nucleus_coords: iNuc >= 0 ? fields[iNuc] || null : null,
          soma_coords: iSoma >= 0 ? fields[iSoma] || null : null,
          notes: iNotes >= 0 ? fields[iNotes] || null : null,
          source_sheet_url: url,
        });
      }

      if (toInsert.length === 0) { error.value = 'No valid segments found'; loading.value = false; return; }

      // Batch insert (Supabase handles up to 1000 rows per insert)
      const batchSize = 500;
      for (let i = 0; i < toInsert.length; i += batchSize) {
        const batch = toInsert.slice(i, i + batchSize);
        const { error: insertErr } = await supabase.from('proofreading_tasks').insert(batch);
        if (insertErr) throw insertErr;
      }

      console.info(`[backend] Imported ${toInsert.length} tasks from Google Sheet`);
      // Reload tasks
      await loadTasks(dataset);
    } catch (e: any) {
      error.value = e.message || 'Failed to import sheet';
      console.warn('[backend] importFromGoogleSheet error:', e.message);
    }
    loading.value = false;
  }

  // ── Load user stats from Supabase → local store ──────────────────────
  /** Hydrate local stats store from Supabase users table. Call after login. */
  async function loadUserStats() {
    if (!userId.value) return;
    try {
      const { data } = await supabase
        .from('users')
        .select('total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak, last_edit_date')
        .eq('id', userId.value)
        .single();
      if (data) {
        const statsStore = useUserStatsStore();
        statsStore.setStats({
          editsAllTime: data.total_edits || 0,
          mergesAllTime: data.total_merges || 0,
          splitsAllTime: data.total_splits || 0,
          cellsSubmitted: data.cells_completed || 0,
          currentStreak: data.current_streak || 0,
          longestStreak: data.longest_streak || 0,
          lastEditDate: data.last_edit_date || '',
        });
        console.info('[backend] Loaded user stats from Supabase');
      }
    } catch (e: any) {
      console.warn('[backend] loadUserStats error:', e.message);
    }
  }

  // ── Leaderboard: top users from Supabase ────────────────────────────
  const leaderboard: Ref<any[]> = ref([]);

  async function loadLeaderboard() {
    try {
      const { data } = await supabase
        .from('users')
        .select('id, display_name, flag, total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak')
        .order('total_edits', { ascending: false })
        .limit(50);
      if (data) {
        leaderboard.value = data;
      }
    } catch (e: any) {
      console.warn('[backend] loadLeaderboard error:', e.message);
    }
  }

  // ── Sync user stats to Supabase ───────────────────────────────────────
  /** Push local stats to the users table. Call periodically or on significant events. */
  async function syncStats() {
    if (!userId.value) return;
    const statsStore = useUserStatsStore();
    const prefsStore = useUserPreferencesStore();
    try {
      await supabase
        .from('users')
        .update({
          total_edits: statsStore.stats.editsAllTime,
          total_merges: statsStore.stats.mergesAllTime,
          total_splits: statsStore.stats.splitsAllTime,
          current_streak: statsStore.stats.currentStreak,
          longest_streak: statsStore.stats.longestStreak,
          last_edit_date: statsStore.stats.lastEditDate || null,
          flag: prefsStore.prefs.flag,
          bio: prefsStore.prefs.bio,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId.value);
    } catch (e: any) {
      console.warn('[backend] syncStats error:', e.message);
    }
  }

  return {
    userId, userEmail, userName, tasks, activeTaskId, activityFeed, loading, error,
    leaderboard,
    syncUser, loadTasks, claimTask, releaseTask, completeTask,
    logEdit, postActivity, subscribeToFeed, unsubscribeFromFeed,
    importFromGoogleSheet, syncStats, loadUserStats, loadLeaderboard,
  };
});

export const useVolumesStore = defineStore('volumes', () => {
  // Hardcoded community volumes — always available regardless of API
  const COMMUNITY_VOLUMES: Volume[] = [
    {
      name: 'pinky',
      description: 'IARPA MICrONS Pinky — mouse visual cortex',
      image_layers: [{
        source: 'precomputed://https://bossdb-open-data.s3.amazonaws.com/iarpa_microns/pinky/em',
        name: 'img',
        description: 'EM imagery',
        type: 'image',
      }],
      segmentation_layers: [{
        source: 'graphene://middleauth+https://minnie.microns-daf.com/segmentation/table/pinky_training3',
        name: 'pinky_training3',
        description: 'Pinky Training 3 segmentation',
        type: 'segmentation',
      }],
    },
    {
      name: 'minnie65',
      description: 'IARPA MICrONS Minnie — 65 column mouse visual cortex',
      image_layers: [{
        source: 'precomputed://https://bossdb-open-data.s3.amazonaws.com/iarpa_microns/minnie/minnie65/em',
        name: 'em',
        description: 'EM imagery',
        type: 'image',
      }],
      segmentation_layers: [{
        source: 'graphene://middleauth+https://minnie.microns-daf.com/segmentation/table/minnie65_public_v117',
        name: 'minnie65_public',
        description: 'Minnie65 public segmentation',
        type: 'segmentation',
      }],
    },
  ];

  const volumes: Ref<Volume[]> = ref([...COMMUNITY_VOLUMES]);

  (async () => {
      if (!CONFIG || !CONFIG.volumes_url) return;
      try {
        const {url, credentialsProvider} = parseSpecialUrl(CONFIG.volumes_url, defaultCredentialsManager);
        const response = await cancellableFetchSpecialOk(credentialsProvider, url, {}, responseJson);

        for (const [key, value] of Object.entries(response as any)) {
          // Skip if we already have a community volume with this name
          if (COMMUNITY_VOLUMES.some(v => v.name === key)) continue;
          volumes.value.push({
            name: key,
            description: (value as any).description,
            image_layers: (value as any).image_layers.map((x: any) => {
              x.type = 'image';
              x.source = x.image_source;
              return x;
            }),
            segmentation_layers: (value as any).segmentation_layers.map((x: any) => {
              x.type = 'segmentation';
              x.source = x.segmentation_source;
              return x;
            }),
          });
        }
      } catch (e) {
        console.warn('[volumes] Failed to fetch volumes from API:', e);
      }
  })();

  return {volumes};
});

// ── Chat ──────────────────────────────────────────────────────────────────────
// Community chat via WebSocket (ported from cj-eyewire-chat-2 branch).
// Uses native WebSocket with auto-reconnect — no external package needed.

export interface ChatMessage {
  type: 'message' | 'join' | 'leave' | 'disconnected' | 'users' | 'time';
  name: string;
  rank: string;
  time: string;
  dateTime: string;
  parts: MessagePart[];
}

interface MessagePart {
  type: 'sender' | 'text' | 'link';
  text: string;
}

const URL_REGEX = /(https?:\/\/[^\s<]+)/g;

export const useChatStore = defineStore('chat', () => {
  const chatMessages = reactive<ChatMessage[]>([]);
  const unreadMessages = ref(false);
  const unreadCount = ref(0);
  const connected = ref(false);
  const minimized = ref(localStorage.getItem('nge_chat_minimized') === 'true');

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = 1000;
  let joined = false;

  // ── Reconnecting WebSocket wrapper ──
  function connect() {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

    const url = (typeof CONFIG !== 'undefined' && CONFIG?.chat_ws_url) || CHAT_WS_URL;
    console.info('[chat] Connecting to', url);

    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.warn('[chat] WebSocket creation failed:', e);
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      console.info('[chat] Connected');
      connected.value = true;
      reconnectDelay = 1000;
      joinChat();
    };

    ws.onmessage = (event) => {
      try {
        handleMessage(JSON.parse(event.data));
      } catch (e) {
        console.warn('[chat] Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.info('[chat] Disconnected');
      connected.value = false;
      joined = false;
      scheduleReconnect();
    };

    ws.onerror = (e) => {
      console.warn('[chat] WebSocket error:', e);
    };
  }

  function disconnect() {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) { ws.onclose = null; ws.close(); ws = null; }
    connected.value = false;
    joined = false;
  }

  function scheduleReconnect() {
    if (reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelay = Math.min(reconnectDelay * 2, 30000);
      connect();
    }, reconnectDelay);
  }

  // ── Join / Send ──
  function joinChat() {
    if (joined || !ws || ws.readyState !== WebSocket.OPEN) return;
    const login = useLoginStore();
    const session = login.sessions.find(s => s.status === undefined);
    const name = session?.name || session?.email?.split('@')[0] || 'Anonymous';
    const userID = session?.email || 'anonymous';
    ws.send(JSON.stringify({ type: 'join', name, userID }));
    joined = true;
  }

  function sendMessage(text: string) {
    if (!text.trim()) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Insert a local disconnected message
      chatMessages.unshift({
        type: 'disconnected',
        name: '', rank: '',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        dateTime: new Date().toISOString(),
        parts: [{ type: 'text', text: 'Not connected — message not sent' }],
      });
      return;
    }
    const login = useLoginStore();
    const session = login.sessions.find(s => s.status === undefined);
    const name = session?.name || session?.email?.split('@')[0] || 'Anonymous';
    const userID = session?.email || 'anonymous';
    ws.send(JSON.stringify({
      type: 'message',
      name,
      userID,
      message: text.trim(),
      timestamp: Date.now(),
    }));
  }

  // ── Handle incoming messages ──
  let lastMessageTime = 0;

  function handleMessage(data: any) {
    const now = Date.now();
    const timeStr = data.timestamp
      ? new Date(data.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Insert time separator if gap > 10 minutes
    if (lastMessageTime > 0 && now - lastMessageTime > 10 * 60 * 1000) {
      chatMessages.unshift({
        type: 'time',
        name: '', rank: '',
        time: timeStr,
        dateTime: new Date().toISOString(),
        parts: [],
      });
    }
    lastMessageTime = now;

    switch (data.type) {
      case 'message': {
        const parts: MessagePart[] = [{ type: 'sender', text: data.name || 'Unknown' }];
        // Split message text on URLs to create link parts
        const msg = data.message || '';
        const segments = msg.split(URL_REGEX);
        for (const seg of segments) {
          if (!seg) continue;
          if (URL_REGEX.test(seg)) {
            parts.push({ type: 'link', text: seg });
          } else {
            parts.push({ type: 'text', text: seg });
          }
          URL_REGEX.lastIndex = 0; // reset global regex
        }
        chatMessages.unshift({
          type: 'message',
          name: data.name || 'Unknown',
          rank: data.rank || '',
          time: timeStr,
          dateTime: new Date().toISOString(),
          parts,
        });
        unreadCount.value++;
        unreadMessages.value = true;
        break;
      }
      case 'join':
        chatMessages.unshift({
          type: 'join', name: data.name || 'Someone', rank: data.rank || '',
          time: timeStr, dateTime: new Date().toISOString(),
          parts: [{ type: 'text', text: `${data.name || 'Someone'} joined` }],
        });
        break;
      case 'leave':
        chatMessages.unshift({
          type: 'leave', name: data.name || 'Someone', rank: data.rank || '',
          time: timeStr, dateTime: new Date().toISOString(),
          parts: [{ type: 'text', text: `${data.name || 'Someone'} left` }],
        });
        break;
      case 'disconnected':
        chatMessages.unshift({
          type: 'disconnected', name: '', rank: '',
          time: timeStr, dateTime: new Date().toISOString(),
          parts: [{ type: 'text', text: 'Disconnected from server' }],
        });
        break;
      case 'users':
        // Could display online users list — skip for now
        break;
      case 'time':
        chatMessages.unshift({
          type: 'time', name: '', rank: '',
          time: timeStr, dateTime: new Date().toISOString(),
          parts: [],
        });
        break;
    }

    // Cap messages at 200 to avoid memory bloat
    if (chatMessages.length > 200) chatMessages.splice(200);
  }

  function markRead() {
    unreadMessages.value = false;
    unreadCount.value = 0;
  }

  function toggleMinimized() {
    minimized.value = !minimized.value;
    localStorage.setItem('nge_chat_minimized', String(minimized.value));
  }

  return {
    chatMessages, unreadMessages, unreadCount, connected, minimized,
    connect, disconnect, sendMessage, markRead, toggleMinimized,
  };
});

// ═══════════════════════════════════════════════════════════════════════════
// Segment Tag Store — Supabase-powered annotation tags
// ═══════════════════════════════════════════════════════════════════════════

import {
  SegmentTag, fetchTagForSegment, setTag as setTagApi,
  clearTag as clearTagApi,
} from './widgets/tag_service';

export {SegmentTag};

export const useSegmentTagStore = defineStore('segmentTags', () => {
  const tag = ref<SegmentTag | null>(null);
  const loading = ref(false);
  const error = ref('');
  const showPanel = ref(true);

  async function loadTag(segmentId: string) {
    if (!segmentId) {tag.value = null; return;}
    loading.value = true;
    error.value = '';
    try {
      tag.value = await fetchTagForSegment(segmentId);
    } catch (e: any) {
      error.value = e.message || 'Failed to load tag';
      console.warn('[tagStore] loadTag error:', e);
    }
    loading.value = false;
  }

  async function saveTag(segmentId: string, tagValue: string, userId?: string, notes?: string) {
    loading.value = true;
    error.value = '';
    try {
      const result = await setTagApi(segmentId, tagValue, userId, notes);
      if (result) tag.value = result;
    } catch (e: any) {
      error.value = e.message || 'Failed to save tag';
      console.warn('[tagStore] saveTag error:', e);
    }
    loading.value = false;
  }

  async function removeTag(segmentId: string) {
    loading.value = true;
    error.value = '';
    try {
      const ok = await clearTagApi(segmentId);
      if (ok && tag.value) tag.value = {...tag.value, tag: ''};
    } catch (e: any) {
      error.value = e.message || 'Failed to clear tag';
    }
    loading.value = false;
  }

  function togglePanel() {showPanel.value = !showPanel.value;}

  return {tag, loading, error, showPanel, loadTag, saveTag, removeTag, togglePanel};
});
